import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import FadeInSection from './FadeInSection';
import ImageFrame from './ImageFrame';

export default function SystemsCarousel({ systems = [] }) {
  const [activeIndex, setActiveIndex] = useState(0);

  if (!systems.length) {
    return null;
  }

  const activeSystem = systems[Math.min(activeIndex, systems.length - 1)];

  return (
    <div id="systems" className="w-full">
      <div className="max-w-7xl mx-auto px-6 pb-16">
        <FadeInSection>
          <div className="text-xs md:text-sm font-bold tracking-[0.4em] uppercase text-[#CBB26A] mb-4 md:mb-6">
            Indicators
          </div>
          <div className="flex flex-wrap gap-2 md:gap-3 mb-10">
            {systems.map((system, idx) => (
              <button
                key={system.id}
                type="button"
                onClick={() => setActiveIndex(idx)}
                className={`px-4 md:px-6 py-2 md:py-3 rounded-full text-sm md:text-base font-semibold transition ${
                  idx === activeIndex
                    ? 'bg-[#CBB26A] text-black shadow-lg shadow-[#CBB26A]/20'
                    : 'bg-neutral-900 ring-1 ring-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                {system.navLabel || system.title}
              </button>
            ))}

            {/* More Systems Link */}
            <Link
              to="/system-library"
              className="px-4 md:px-6 py-2 md:py-3 rounded-full text-sm md:text-base font-semibold transition bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-white hover:from-[#D8C585] hover:to-[#BDA45E] shadow-lg shadow-[#CBB26A]/20"
            >
              More Systems →
            </Link>
          </div>

          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSystem.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <SystemPanel system={activeSystem} />
              </motion.div>
            </AnimatePresence>
          </div>
        </FadeInSection>
      </div>
    </div>
  );
}

function SystemPanel({ system }) {
  const {
    title,
    subtitle,
    description,
    metrics = [],
    cta,
    wrapperClass = '',
    layout = {},
    images = [],
    defaultImageId,
    marketStates = [],
    marketStatesLabel,
  } = system;

  const [activeImageId, setActiveImageId] = useState(
    defaultImageId || images[0]?.id || null
  );
  const activeImage =
    images.find((image) => image.id === activeImageId) || images[0] || null;
  const [activeMarketStateId, setActiveMarketStateId] = useState(
    marketStates[0]?.id || null
  );

  const containerClass =
    layout.containerClass ?? 'flex flex-col md:flex-row gap-8 md:gap-12 items-start';
  const textColumnClass = layout.textColumnClass ?? 'flex-1 max-w-3xl';
  const imageColumnClass = layout.imageColumnClass ?? 'flex-1';
  const textFirst = layout.textFirst !== false;

  useEffect(() => {
    setActiveImageId(defaultImageId || images[0]?.id || null);
  }, [defaultImageId, images]);

  useEffect(() => {
    setActiveMarketStateId(marketStates[0]?.id || null);
  }, [marketStates]);

  const textColumn = (
    <div className={`${textColumnClass} space-y-4 pr-1 sm:pr-2`}>
      <div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black leading-tight mb-3">
              {title}
            </h2>
            {subtitle && (
              <p className="text-lg md:text-xl text-[#CBB26A]">{subtitle}</p>
            )}
          </div>
          {description && (
            <p className="text-base md:text-lg text-neutral-300 leading-relaxed">
              {description}
            </p>
          )}

          {marketStates.length > 0 && (
            <div>
              <div className="text-xs font-bold tracking-wider uppercase text-neutral-500 mb-3">
                {marketStatesLabel || 'Market States'}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {marketStates.map((state) => (
                  <button
                    key={state.id}
                    type="button"
                    onClick={() => setActiveMarketStateId(state.id)}
                    className={`p-3 text-left rounded-lg transition ${
                      activeMarketStateId === state.id
                        ? state.activeClass
                        : state.inactiveClass
                    }`}
                  >
                    <div
                      className={`${state.accentClass} font-bold text-xs md:text-sm mb-0.5`}
                    >
                      {state.label}
                    </div>
                    <div className="text-[10px] md:text-xs text-neutral-400">
                      {state.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {metrics.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              {metrics.map((metric) => (
                <div
                  key={metric.label}
                  className="bg-neutral-900/50 ring-1 ring-neutral-800/50 hover:ring-[#CBB26A]/30 p-3 rounded-lg transition-all"
                >
                  <div className="text-[#CBB26A] font-bold text-xs md:text-sm mb-1">
                    {metric.label}
                  </div>
                  <div className="text-[10px] md:text-xs text-neutral-400">
                    {metric.desc}
                  </div>
                </div>
              ))}
            </div>
          )}

      {cta && (
        <a
          href={cta.href}
          className="inline-flex items-center gap-2 text-sm text-[#CBB26A] font-semibold hover:text-[#CBB26A] transition mt-2"
        >
          {cta.label} <ArrowRight className="w-4 h-4" />
        </a>
      )}
    </div>
  );

  const imageColumn =
    activeImage && (
      <div className={`${imageColumnClass} space-y-4 pr-2 sm:pr-4 md:pr-6`}>
        {images.length > 1 && (
          <div className="flex justify-center md:justify-end gap-2 flex-wrap">
            {images.map((image) => (
              <button
                key={image.id}
                type="button"
                onClick={() => setActiveImageId(image.id)}
                className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition ${
                  activeImageId === image.id
                    ? 'bg-[#CBB26A] text-black'
                    : 'bg-neutral-900 ring-1 ring-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                {image.label}
              </button>
            ))}
          </div>
        )}
        <ImageFrame
          src={activeImage.src}
          alt={activeImage.alt}
          caption={activeImage.caption}
        />
      </div>
    );

  return (
    <div className={`${wrapperClass} rounded-2xl`}>
      <div className={containerClass}>
        {textFirst ? (
          <>
            {textColumn}
            {imageColumn}
          </>
        ) : (
          <>
            {imageColumn}
            {textColumn}
          </>
        )}
      </div>
    </div>
  );
}
